import React from "react";
import { Link } from "react-router-dom";
import {
  Flame,
  Home,
  Wrench,
  Zap,
  Star,
  ShieldCheck,
  ArrowRight,
  MapPin,
  CheckCircle2,
  ChevronDown,
} from "lucide-react";
import {
  businessInfo,
  homepageHero,
  trustPoints,
  homepageServices,
  reviews,
  meetLee,
  landlordServicesList,
  heroImage,
  pageHeroImages,
} from "../data/mockData";
import { ContactButtons } from "../components/ContactButtons";
import { TrustList } from "../components/TrustList";
import ReviewCard from "../components/ReviewCard";
import FinalCTA from "../components/FinalCTA";
import Seo from "../components/Seo";
import { PAGE_SEO, SITE_URL } from "../lib/seo";

const iconMap = { flame: Flame, home: Home, wrench: Wrench, zap: Zap };

const HomePage = () => {
  const homeSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: PAGE_SEO.home.title,
    url: `${SITE_URL}/`,
    isPartOf: { "@type": "WebSite", url: SITE_URL, name: "TOFT Gas, Heating & Plumbing" },
  };
  return (
    <>
      <Seo {...PAGE_SEO.home} jsonLd={homeSchema} />
      {/* Hero — Photo-forward, full-bleed (Inner Style inspired) */}
      <section className="relative min-h-[80vh] lg:min-h-[88vh] flex items-center text-white overflow-hidden">
        {/* Full-bleed cinematic background */}
        <div className="absolute inset-0">
          <div
            className="absolute inset-0 ken-burns"
            style={{
              backgroundImage: `url(${heroImage})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
        </div>

        {/* Gradient overlay — lets the photo breathe */}
        <div className="absolute inset-0 bg-gradient-to-b from-brand-navy-dark/70 via-brand-navy/40 to-brand-navy-dark/85" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-navy-dark/70 via-brand-navy/30 to-transparent" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24 w-full">
          <div className="max-w-3xl">
            {/* Rating chip */}
            <div className="inline-flex items-center gap-2 bg-white/95 backdrop-blur rounded-full pl-1.5 pr-4 py-1.5 mb-7 shadow-lg">
              <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-amber-400">
                <Star className="h-3.5 w-3.5 fill-white text-white" />
              </span>
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-3 w-3 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <span className="text-xs font-bold text-brand-navy">
                5.0
              </span>
              <span className="text-slate-300">·</span>
              <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">
                Gas Safe Engineer
              </span>
            </div>

            {/* Editorial H1 */}
            <h1
              data-testid="hero-h1"
              className="font-black tracking-tight leading-[0.98] mb-6 text-white drop-shadow-lg"
            >
              <span className="block text-5xl sm:text-6xl lg:text-7xl xl:text-[5.25rem]">
                Boiler Servicing &amp;
              </span>
              <span className="block text-5xl sm:text-6xl lg:text-7xl xl:text-[5.25rem]">
                CP12 Certificates
              </span>
              <span className="block mt-2 text-2xl sm:text-3xl lg:text-[2.5rem] font-bold italic text-brand-blue-light leading-tight">
                Cardiff · Pontypridd · Porth · Bridgend · Caerphilly
              </span>
            </h1>

            {/* Lead */}
            <p className="text-lg sm:text-xl text-slate-100 max-w-2xl mb-3 leading-relaxed drop-shadow">
              Boiler servicing, repairs and CP12 certificates from a Gas Safe
              engineer who actually turns up when he says he will.
            </p>
            <p className="text-base text-slate-200 max-w-2xl mb-8 leading-relaxed drop-shadow">
              Based in Porth · Covering Rhondda Cynon Taf, Cardiff, Bridgend &amp;
              Caerphilly.
            </p>

            {/* Compact pricing pills */}
            <div className="flex flex-wrap gap-2 mb-8">
              {[
                "Boiler Servicing from £70",
                "CP12 from £70",
                "Free Replacement Quotes",
              ].map((label) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-4 py-1.5 text-sm font-semibold"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-brand-blue-light" />
                  {label}
                </span>
              ))}
            </div>

            {/* CTAs */}
            <ContactButtons variant="large" />

            {/* Live availability */}
            <div className="mt-7 flex items-center gap-2 text-sm">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-whatsapp opacity-75" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-brand-whatsapp" />
              </span>
              <span className="font-bold text-white">Lee is available now</span>
              <span className="text-white/40">·</span>
              <span className="text-slate-200">Most enquiries answered within the hour</span>
            </div>
          </div>
        </div>

        {/* Scroll down arrow */}
        <button
          onClick={() =>
            window.scrollTo({ top: window.innerHeight * 0.85, behavior: "smooth" })
          }
          data-testid="hero-scroll-down"
          aria-label="Scroll down"
          className="absolute bottom-8 left-1/2 -translate-x-1/2 h-12 w-12 rounded-full bg-white/15 backdrop-blur-md border border-white/30 hover:bg-white/25 flex items-center justify-center transition-colors group"
        >
          <ChevronDown className="h-6 w-6 text-white animate-bounce group-hover:animate-none" />
        </button>
      </section>

      {/* Stats strip — separated from hero */}
      <section className="bg-brand-navy-dark text-white py-8 lg:py-10" data-testid="stats-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 lg:gap-10">
            {[
              { num: "5.0★", label: "Customer Rated" },
              { num: "£70", label: "Servicing From" },
              { num: "4", label: "Counties Covered" },
              { num: "100%", label: "Gas Safe Certified" },
            ].map((s) => (
              <div key={s.label} className="text-center sm:text-left">
                <div className="text-3xl sm:text-4xl lg:text-5xl font-black mb-1 tracking-tight">
                  {s.num}
                </div>
                <div className="text-[10px] sm:text-xs font-bold uppercase tracking-widest text-slate-400">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Boiler brands we service */}
      <section className="bg-white border-b border-slate-100 py-8" data-testid="brands-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-5">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500 whitespace-nowrap">
              We service all major boiler brands
            </p>
            <div className="flex flex-wrap items-center justify-center gap-x-7 gap-y-3">
              {["Worcester Bosch", "Vaillant", "Baxi", "Ideal", "Glow-worm", "Viessmann"].map(
                (brand) => (
                  <span
                    key={brand}
                    className="text-base sm:text-lg font-black text-slate-400 tracking-tight hover:text-brand-navy transition-colors"
                  >
                    {brand}
                  </span>
                )
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="bg-brand-slate-light py-12 lg:py-16" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-5 w-5 fill-amber-400 text-amber-400" />
              ))}
            </div>
            <p className="text-sm sm:text-base font-bold text-brand-navy uppercase tracking-wide">
              Trusted By Homeowners & Landlords
            </p>
          </div>
          <TrustList items={trustPoints} columns={3} testId="hero-trust-list" />
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 lg:py-24" data-testid="why-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-brand-navy mb-6 tracking-tight">
            Why Choose TOFT Gas, Heating & Plumbing?
          </h2>
          <div className="space-y-4 text-base sm:text-lg text-slate-700 leading-relaxed">
            <p>
              When you're looking for a gas engineer, you're not just looking for someone
              with the right qualifications.
            </p>
            <p>
              You want somebody who turns up when they say they will, communicates clearly,
              provides honest advice and takes pride in their work.
            </p>
            <p className="font-semibold text-brand-navy">
              That's exactly what we aim to provide.
            </p>
            <p>
              Based in Porth and covering South Wales, we work with homeowners, landlords,
              HMO owners and property investors across Cardiff, Pontypridd, Porth, Bridgend,
              Caerphilly and Rhondda Cynon Taf — who want a reliable engineer they can trust
              year after year.
            </p>
            <p>
              Whether it's an annual boiler service, a landlord gas safety certificate, a
              boiler fault or a heating problem, our focus is always the same:
            </p>
            <p className="text-xl sm:text-2xl font-bold text-brand-blue pt-2">
              Reliable service. Honest advice. Quality workmanship.
            </p>
          </div>
        </div>
      </section>

      {/* Meet Lee */}
      <section className="bg-brand-blue-soft py-16 lg:py-24" data-testid="meet-lee">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-start">
            {/* Image side */}
            <div className="lg:col-span-2">
              <div className="relative">
                <div className="absolute -inset-3 bg-brand-navy/10 rounded-2xl rotate-2" />
                <div
                  className="relative aspect-[4/5] rounded-2xl bg-cover bg-center shadow-2xl"
                  style={{
                    backgroundImage:
                      "url(https://images.unsplash.com/photo-1542013936693-884638332954?w=900&q=85&auto=format&fit=crop)",
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/80 via-transparent to-transparent rounded-2xl" />
                  <div className="absolute bottom-5 left-5 right-5 text-white">
                    <div className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur border border-white/30 rounded-full px-3 py-1.5 mb-2">
                      <ShieldCheck className="h-3.5 w-3.5 text-brand-blue-light" />
                      <span className="text-[10px] font-bold tracking-widest uppercase">
                        Gas Safe Engineer
                      </span>
                    </div>
                    <p className="text-xl font-bold">Lee</p>
                    <p className="text-sm text-slate-200">Founder · TOFT Gas, Heating & Plumbing</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Text side */}
            <div className="lg:col-span-3 bg-white rounded-2xl shadow-lg p-7 lg:p-10">
              <div className="text-xs font-bold tracking-widest uppercase text-brand-blue mb-3">
                The Engineer
              </div>
              <h2 className="text-3xl sm:text-4xl font-black text-brand-navy mb-5 tracking-tight">
                Meet Lee
              </h2>
              <p className="text-lg sm:text-xl font-bold text-brand-navy mb-4">{meetLee.intro}</p>
              <div className="space-y-3 text-base text-slate-700 leading-relaxed mb-5">
                {meetLee.body.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
              <div className="bg-brand-blue-soft border-l-4 border-brand-blue rounded p-4 mb-6">
                <p className="text-xs font-bold uppercase tracking-wider text-brand-blue mb-1">
                  My goal is simple
                </p>
                <p className="text-base sm:text-lg font-bold text-brand-navy">{meetLee.goal}</p>
              </div>
              <Link
                to="/contact"
                data-testid="about-lee-btn"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-brand-navy text-white font-semibold hover:bg-brand-navy-light transition-colors"
              >
                Get In Touch With Lee
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 lg:py-24" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <p className="text-xs font-bold tracking-widest uppercase text-brand-blue mb-3">
              What We Do
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-brand-navy tracking-tight">
              Our Most Popular Services
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6">
            {homepageServices.map((s) => {
              const Icon = iconMap[s.iconKey] || Wrench;
              return (
                <Link
                  to={s.href}
                  key={s.id}
                  data-testid={`service-card-${s.id}`}
                  className="group block bg-white rounded-2xl border border-slate-200 p-7 hover:border-brand-blue hover:shadow-xl transition-all"
                >
                  <div className="flex items-start gap-5">
                    <div className="shrink-0 h-14 w-14 rounded-xl bg-brand-blue-soft text-brand-blue flex items-center justify-center group-hover:bg-brand-blue group-hover:text-white transition-colors">
                      <Icon className="h-7 w-7" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-brand-navy mb-2">{s.title}</h3>
                      <p className="text-slate-600 leading-relaxed mb-4">{s.description}</p>
                      <span className="inline-flex items-center gap-1.5 text-sm font-bold text-brand-blue group-hover:gap-2.5 transition-all">
                        Learn More <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Landlord Section */}
      <section className="py-16 lg:py-24 bg-brand-navy text-white" data-testid="landlord-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs font-bold tracking-widest uppercase text-brand-blue-light mb-3">
                For Property Owners
              </p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-6 tracking-tight">
                Landlord Services
              </h2>
              <div className="space-y-4 text-base sm:text-lg text-slate-200 leading-relaxed mb-7">
                <p>
                  We understand landlords because we regularly work with rental properties
                  and are landlords ourselves.
                </p>
                <p>
                  We know how important it is to have reliable contractors who communicate
                  properly, attend when arranged and provide documentation promptly.
                </p>
              </div>
              <Link
                to="/landlord-services"
                data-testid="landlord-cta"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-white text-brand-navy font-semibold hover:bg-brand-blue-soft transition-colors"
              >
                Landlord Services
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-7">
              <p className="text-sm font-bold uppercase tracking-widest text-brand-blue-light mb-5">
                Our Landlord Services Include
              </p>
              <ul className="space-y-3">
                {landlordServicesList.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-base sm:text-lg">
                    <CheckCircle2 className="h-5 w-5 text-brand-blue-light shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-sm text-slate-300 italic">
                Whether you own one property or a larger portfolio, we're here to help.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="py-16 lg:py-24" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <p className="text-xs font-bold tracking-widest uppercase text-brand-blue mb-3">
                What Our Customers Say
              </p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-brand-navy tracking-tight">
                Customer Reviews
              </h2>
            </div>
            <Link
              to="/reviews"
              data-testid="view-all-reviews"
              className="inline-flex items-center gap-2 text-sm font-bold text-brand-blue hover:text-brand-navy"
            >
              View All Reviews <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6">
            {reviews.slice(0, 4).map((r, i) => (
              <ReviewCard review={r} key={r.id} featured={i === 0} />
            ))}
          </div>
        </div>
      </section>

      {/* Areas */}
      <section className="bg-brand-slate-light py-16 lg:py-20" data-testid="areas-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-1.5 mb-5">
            <MapPin className="h-4 w-4 text-brand-blue" />
            <span className="text-xs font-bold tracking-widest uppercase text-brand-blue">
              Areas Covered
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-brand-navy mb-5 tracking-tight">
            Gas Safe Engineer Covering Cardiff, Pontypridd, Porth, Bridgend &amp; Caerphilly
          </h2>
          <p className="text-lg text-slate-600 mb-7 max-w-2xl mx-auto">
            We regularly work across:
          </p>
          <div className="flex flex-wrap justify-center gap-2 mb-7">
            {businessInfo.serviceAreas.map((a) => (
              <span
                key={a}
                className="bg-white border border-slate-200 px-4 py-2 rounded-full text-sm font-bold text-brand-navy"
              >
                {a}
              </span>
            ))}
          </div>
          <p className="text-sm text-slate-500 mb-7 max-w-2xl mx-auto">
            Including {businessInfo.serviceTowns.slice(0, 6).join(", ")} and surrounding areas.
          </p>
          <Link
            to="/areas-covered"
            data-testid="view-areas-btn"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-brand-navy text-white font-semibold hover:bg-brand-navy-light transition-colors"
          >
            View Areas Covered
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <FinalCTA
        title="Ready to book your service?"
        lines={[
          "Need a boiler service?",
          "Need a landlord gas safety certificate?",
          "Need help with a boiler fault?",
          "Looking for a Gas Safe engineer you can rely on?",
        ]}
      />
    </>
  );
};

export default HomePage;
